import { useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { api, type ArchivoCompartido, type EnlaceEntrega } from '../api';
import { useApi, mensajeDeError, VIVO } from '../lib/useApi';
import { useEsAdmin } from '../lib/sesion';
import { useEsCelular } from '../lib/useMedia';
import { Cambia,
  Buscador,
  Cargando,
  Encabezado,
  EstadoChip,
  filtrar,
  Icono,
  Numero,
  Paginador,
  usePaginado,
  PanelError,
  useConfirmar,
  Vacio,
} from '../components/ui';
import { abrir, CURVA, desplegar, fila, revelarDesde } from '../components/movimiento';
import {
  aviso,
  botonPrimario,
  botonSecundario,
  c,
  campo,
  encabezadoTabla,
  etiquetaCampo,
  fechaCorta,
  filaTabla,
  mono,
  nota as notaCaja,
  tarjeta,
  type Tono,
  HUSO,
} from '../theme';

/**
 * El buzón de archivos con ZF.
 *
 * Nació de un cartel: la política de seguridad de ZF le bloquea a su gente la
 * subida a Google Drive y sólo les habilita Teams, SharePoint y OneDrive —que
 * son de ellos—, así que el canal por el que pasaban las planillas se cortó.
 * Acá el archivo entra al sistema del depósito, que ya corre en un dominio de
 * RDM y con HTTPS.
 *
 * Lo que se sube **no se importa**: se guarda para que otra persona lo
 * descargue y lo revise. La planilla de productos no trae posiciones ni
 * cantidades, así que no hay nada que el sistema pueda hacer sola con ella.
 *
 * Los tres roles suben y descargan; borrar es de administradores, para que
 * nadie haga desaparecer el archivo del otro.
 */


const cuando = (iso: string) =>
  new Date(iso).toLocaleString('es-AR', {
    timeZone: HUSO,
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

/**
 * Las celdas de las tablas, con las medidas de las filas de las otras listas
 * pero sin `display` en línea: el celular lo cambia por CSS (`.wms-tarjetas`) y
 * un estilo en línea le ganaría.
 */
const { display: _d1, alignItems: _a1, gap: _g1, ...celda } = filaTabla;
const { display: _d2, alignItems: _a2, gap: _g2, ...celdaTitulo } = encabezadoTabla;
const titulo = { ...celdaTitulo, textAlign: 'left' } as const;

const TONO_ENLACE: Record<EnlaceEntrega['estado'], Tono> = {
  vigente: 'ok',
  agotado: 'warn',
  vencido: 'neu',
  revocado: 'neu',
};

export default function Archivos() {
  const esAdmin = useEsAdmin();
  // El buzón es de ida y vuelta con el cliente: lo que sube el otro aparece solo.
  const buzon = useApi(() => api.archivos(), [], VIVO.normal);
  const enlaces = useApi(
    () => (esAdmin ? api.enlacesEntrega() : Promise.resolve({ enlaces: [] as EnlaceEntrega[] })),
    [esAdmin],
  );

  const [texto, setTexto] = useState('');
  const [error, setError] = useState('');
  const [subido, setSubido] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [borrando, setBorrando] = useState<number | null>(null);
  /** El archivo que se está borrando: sólo ése sale con animación (ver la fila). */
  const [quitando, setQuitando] = useState<number | null>(null);
  const entrada = useRef<HTMLInputElement>(null);

  async function subir(f: File | undefined) {
    if (!f) return;
    setOcupado(true);
    setError('');
    setSubido('');
    try {
      const r = await api.subirArchivo(f, texto);
      setSubido(`${r.nombre} · ${r.peso}`);
      setTexto('');
      buzon.recargar();
    } catch (err) {
      setError(mensajeDeError(err));
    } finally {
      setOcupado(false);
    }
  }

  async function descargar(a: ArchivoCompartido) {
    setError('');
    try {
      await api.descargarArchivo(a.id, a.nombre);
    } catch (err) {
      setError(mensajeDeError(err));
    }
  }

  async function borrar(id: number) {
    setError('');
    setBorrando(null);
    setQuitando(id);
    try {
      await api.borrarArchivo(id);
      buzon.recargar();
      enlaces.recargar();
    } catch (err) {
      setError(mensajeDeError(err));
    }
  }

  const archivos = buzon.datos?.archivos ?? [];

  const [busqueda, setBusqueda] = useState('');
  const encontrados = filtrar(archivos, busqueda, (a) => [a.nombre, a.nota, a.subidoPor, a.subidoEn]);
  const pagina = usePaginado(encontrados);
  const accept = (buzon.datos?.extensiones ?? ['.xlsx', '.xls', '.csv', '.pdf']).join(',');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 1100 }}>
      <Encabezado
        bajada="El lugar para dejar y retirar planillas y documentación. Lo que se sube acá no se importa: queda guardado para que otra persona lo descargue y lo revise."
      >
        <label style={{ ...botonPrimario, position: 'relative', minHeight: 48 }}>
          <Icono nombre="upload_file" size={19} />
          <Cambia clave={ocupado ? 'subiendo' : 'subir'}>{ocupado ? 'Subiendo…' : 'Subir archivo'}</Cambia>
          <input
            ref={entrada}
            type="file"
            accept={accept}
            disabled={ocupado}
            onChange={(e) => {
              void subir(e.target.files?.[0]);
              e.target.value = '';
            }}
            style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
          />
        </label>
      </Encabezado>

      {error && <PanelError mensaje={error} />}

      <AnimatePresence>
      {subido && (
        <motion.div key={subido} {...abrir(16)} role="status" style={{ ...aviso('ok'), display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <Icono nombre="task_alt" size={24} color={c.verde} />
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: c.texto }}>Archivo subido</div>
            <div style={{ marginTop: 2, fontSize: 13, color: c.texto2 }}>{subido}</div>
          </div>
        </motion.div>
      )}
      </AnimatePresence>

      <div style={{ ...tarjeta, padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <label style={etiquetaCampo} htmlFor="nota-archivo">
          Nota para el que lo va a revisar (opcional)
        </label>
        <input
          id="nota-archivo"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          maxLength={300}
          placeholder="Maestro de artículos de septiembre, sin los descontinuados"
          style={campo}
        />
        <span style={{ fontSize: 12.5, color: c.texto3 }}>
          Se escribe antes de elegir el archivo y viaja con él. Se aceptan {accept.split(',').join(' ')}, hasta 25 MB.
        </span>
      </div>

      <div style={{ ...tarjeta, overflow: 'hidden' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
            padding: '14px 16px',
            borderBottom: `1px solid ${c.borde}`,
          }}
        >
          <span style={{ fontSize: 16, fontWeight: 600, color: c.texto }}>En el buzón</span>
          <span style={{ flex: 1, minWidth: 200, display: 'flex', justifyContent: 'flex-end' }}>
            {archivos.length > 0 && (
              <Buscador valor={busqueda} onCambiar={setBusqueda} placeholder="Nombre, nota o quién lo subió" ancho={320} />
            )}
          </span>
          {archivos.length > 0 && <EstadoChip estado={`${archivos.length} archivos`} tono="neu" />}
        </div>

        {buzon.cargando && <Cargando texto="Buscando los archivos…" />}
        {buzon.error && <PanelError mensaje={buzon.error} onReintentar={buzon.recargar} />}

        {!buzon.cargando && !buzon.error && archivos.length === 0 && (
          <Vacio
            icono="folder_open"
            titulo="Todavía no hay ningún archivo"
            detalle={
              esAdmin
                ? 'Subí el primero con el botón de arriba, o generá un enlace de entrega para que lo deje alguien de ZF sin tener que iniciar sesión.'
                : 'Subí el primero con el botón de arriba. Queda acá para que lo descargue quien lo tenga que revisar.'
            }
          />
        )}

        {archivos.length > 0 && (
          <div className="wms-scroll-x">
            <table className="wms-tarjetas" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: c.fondo, borderBottom: `1px solid ${c.borde}` }}>
                  <th style={{ ...titulo, minWidth: 240 }}>Archivo</th>
                  <th style={{ ...titulo, width: 90, textAlign: 'right' }}>Peso</th>
                  <th style={{ ...titulo, minWidth: 180 }}>Lo subió</th>
                  <th style={{ ...titulo, width: 130 }}>Cuándo</th>
                  <th style={{ ...titulo, width: esAdmin ? 210 : 130 }} />
                </tr>
              </thead>
              <tbody>
                {/* Sólo sale con animación el que se borró: al pasar de página
                    las filas viejas no tienen que quedarse una vez más en pantalla. */}
                <AnimatePresence>
                  {pagina.visibles.map((a, n) => (
                    <motion.tr
                      key={a.id}
                      {...fila(n)}
                      // El archivo nuevo cae desde arriba y las demás filas se corren (`layout`);
                      // al borrar, sale ése y las de abajo suben.
                      layout="position"
                      initial={{ opacity: 0, y: -16 }}
                      exit={quitando === a.id ? fila(0).exit : undefined}
                    >
                      <td className="wms-principal" style={celda}>
                        <span style={{ display: 'grid', gap: 2 }}>
                          <span style={{ fontWeight: 500, wordBreak: 'break-all' }}>{a.nombre}</span>
                          {a.nota && <span style={{ fontSize: 12.5, color: c.texto2, textWrap: 'pretty' }}>{a.nota}</span>}
                        </span>
                      </td>
                      <td
                        data-rotulo="Peso"
                        style={{ ...celda, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: c.texto2 }}
                      >
                        {a.peso}
                      </td>
                      <td data-rotulo="Lo subió" style={{ ...celda, fontSize: 12.5, color: c.texto2, wordBreak: 'break-all' }}>
                        {a.subidoPor}
                        {a.porEnlace && <span style={{ color: c.texto3 }}> · por enlace</span>}
                      </td>
                      <td
                        data-rotulo="Cuándo"
                        style={{ ...celda, fontSize: 12.5, color: c.texto2, fontVariantNumeric: 'tabular-nums' }}
                      >
                        {cuando(a.subidoEn)}
                      </td>
                      <td className="wms-acciones" style={celda}>
                        <span className="flex flex-wrap justify-start gap-2 tablet:justify-end">
                          <button
                            type="button"
                            onClick={() => void descargar(a)}
                            style={{ ...botonSecundario, padding: '8px 14px', minHeight: 0 }}
                          >
                            <Icono nombre="download" size={17} />
                            Descargar
                          </button>
                          {esAdmin && (
                            <AnimatePresence mode="wait" initial={false}>
                              <motion.span
                                key={borrando === a.id ? 'confirma' : 'borra'}
                                initial={{ opacity: 0, y: 5 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -5 }}
                                transition={{ duration: 0.16, ease: CURVA }}
                                style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 8 }}
                              >
                            {borrando === a.id ? (
                              <>
                                <button
                                  type="button"
                                  onClick={() => void borrar(a.id)}
                                  style={{ ...botonSecundario, padding: '8px 14px', minHeight: 0, color: c.rojo }}
                                >
                                  Confirmar
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setBorrando(null)}
                                  style={{ ...botonSecundario, padding: '8px 14px', minHeight: 0 }}
                                >
                                  No
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setBorrando(a.id)}
                                style={{ ...botonSecundario, padding: '8px 14px', minHeight: 0, color: c.texto2 }}
                              >
                                <Icono nombre="delete" size={17} />
                                Borrar
                              </button>
                            )}
                              </motion.span>
                            </AnimatePresence>
                          )}
                        </span>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        )}
        <Paginador
          pagina={pagina.pagina}
          paginas={pagina.paginas}
          total={pagina.total}
          desde={pagina.desde}
          que="archivos"
          onIr={pagina.setPagina}
        />
      </div>

      {esAdmin && <Enlaces estado={enlaces} />}
    </div>
  );
}

type EstadoEnlaces = {
  datos: { enlaces: EnlaceEntrega[] } | null;
  cargando: boolean;
  error: string | null;
  recargar: () => void;
};

/**
 * Los enlaces de entrega, sólo para administradores.
 *
 * Un enlace es la puerta para quien no puede iniciar sesión: el sistema entra
 * por Google y ZF es un entorno Microsoft, así que sus cuentas no tienen
 * identidad de Google. Con el link alcanza para dejar un archivo, y no sirve
 * para nada más.
 */
function Enlaces({ estado }: { estado: EstadoEnlaces }) {
  const [rotulo, setRotulo] = useState('');
  const [dias, setDias] = useState(30);
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [nuevo, setNuevo] = useState('');
  const [copiado, setCopiado] = useState(false);
  const [confirmar, dialogo] = useConfirmar();
  const celular = useEsCelular();

  async function crear() {
    setOcupado(true);
    setError('');
    setNuevo('');
    setCopiado(false);
    try {
      const r = await api.crearEnlace(rotulo, dias);
      // El link se arma acá porque el servidor no sabe con qué dominio se está
      // entrando, y el token va en el fragmento: así no queda escrito en el log
      // de ningún servidor ni en el del proxy del cliente.
      setNuevo(`${window.location.origin}${window.location.pathname}#/entrega/${r.token}`);
      setRotulo('');
      estado.recargar();
    } catch (err) {
      setError(mensajeDeError(err));
    } finally {
      setOcupado(false);
    }
  }

  async function revocar(e: EnlaceEntrega) {
    const ok = await confirmar({
      titulo: `¿Dar de baja el enlace de «${e.rotulo}»?`,
      detalle: 'Deja de servir desde ya: quien lo tenga no va a poder subir más archivos. Lo que ya subió queda.',
      boton: 'Dar de baja el enlace',
    });
    if (!ok) return;
    setError('');
    try {
      await api.revocarEnlace(e.id);
      estado.recargar();
    } catch (err) {
      setError(mensajeDeError(err));
    }
  }

  async function copiar() {
    try {
      await navigator.clipboard.writeText(nuevo);
      setCopiado(true);
    } catch {
      setError('No pudimos copiarlo solos: seleccioná el enlace y copialo a mano.');
    }
  }

  const enlaces = estado.datos?.enlaces ?? [];

  return (
    <div style={{ ...tarjeta, overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px', borderBottom: `1px solid ${c.borde}` }}>
        <span style={{ fontSize: 16, fontWeight: 600, color: c.texto }}>Enlaces de entrega</span>
        <div style={{ marginTop: 4, fontSize: 13, color: c.texto2, textWrap: 'pretty' }}>
          Para que alguien de ZF deje un archivo sin tener usuario en el sistema. El enlace se manda por Teams o por
          mail, sirve sólo para subir, y vence.
        </div>
      </div>

      <div style={{ padding: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: 1, minWidth: 220, display: 'grid', gap: 6 }}>
          <label style={etiquetaCampo} htmlFor="rotulo-enlace">
            Para quién es
          </label>
          <input
            id="rotulo-enlace"
            value={rotulo}
            onChange={(e) => setRotulo(e.target.value)}
            maxLength={60}
            placeholder="Compras ZF"
            style={campo}
          />
        </div>
        <div style={{ width: 130, display: 'grid', gap: 6 }}>
          <label style={etiquetaCampo} htmlFor="dias-enlace">
            Días que dura
          </label>
          <input
            id="dias-enlace"
            type="number"
            inputMode="numeric"
            min={1}
            max={365}
            value={dias}
            onChange={(e) => setDias(Number(e.target.value))}
            style={campo}
          />
        </div>
        <button
          type="button"
          onClick={() => void crear()}
          disabled={ocupado || !rotulo.trim()}
          style={{ ...botonPrimario, opacity: ocupado || !rotulo.trim() ? 0.5 : 1 }}
        >
          <Icono nombre="link" size={19} />
          <Cambia clave={ocupado ? 'generando' : 'generar'}>{ocupado ? 'Generando…' : 'Generar enlace'}</Cambia>
        </button>
      </div>

      {error && (
        <div style={{ padding: '0 16px 16px' }}>
          <PanelError mensaje={error} />
        </div>
      )}

      <AnimatePresence>
      {nuevo && (
        <motion.div key="nuevo" {...desplegar}>
        {/* Nace en el botón «Generar enlace», que está arriba y a la derecha. */}
        <motion.div {...revelarDesde(celular ? '50%' : 'calc(100% - 120px)', '-50px')}>
        <div style={{ padding: '0 16px 16px', display: 'grid', gap: 8 }}>
          <div style={{ ...aviso('warn'), display: 'grid', gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: c.texto }}>
              Copialo ahora: no se vuelve a mostrar
            </span>
            <span style={{ fontSize: 13, color: c.texto2, textWrap: 'pretty' }}>
              El sistema guarda el enlace cifrado, así que si se pierde hay que generar otro. Mandalo por Teams o por
              mail; cualquiera que lo tenga puede dejar archivos hasta que venza.
            </span>
            <div style={{ ...notaCaja, fontFamily: mono, fontSize: 12, wordBreak: 'break-all', color: c.texto }}>
              {nuevo}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" onClick={() => void copiar()} style={botonSecundario}>
                <Cambia clave={copiado ? 'copiado' : 'copiar'}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <Icono nombre={copiado ? 'check' : 'content_copy'} size={18} />
                    {copiado ? 'Copiado' : 'Copiar enlace'}
                  </span>
                </Cambia>
              </button>
              <button type="button" onClick={() => setNuevo('')} style={botonSecundario}>
                Listo
              </button>
            </div>
          </div>
        </div>
        </motion.div>
        </motion.div>
      )}
      </AnimatePresence>

      {estado.cargando && <Cargando texto="Buscando los enlaces…" />}
      {estado.error && <PanelError mensaje={estado.error} onReintentar={estado.recargar} />}

      {enlaces.length > 0 && (
        <div className="wms-scroll-x">
          <table className="wms-tarjetas" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: c.fondo, borderBottom: `1px solid ${c.borde}` }}>
                <th style={{ ...titulo, minWidth: 180 }}>Para quién</th>
                <th style={{ ...titulo, width: 120 }}>Estado</th>
                <th style={{ ...titulo, width: 110, textAlign: 'right' }}>Archivos</th>
                <th style={{ ...titulo, width: 120 }}>Vence</th>
                <th style={{ ...titulo, width: 120 }} />
              </tr>
            </thead>
            <tbody>
              <AnimatePresence initial={false}>
              {enlaces.map((e, n) => (
                <motion.tr key={e.id} {...fila(n)} layout="position">
                  <td className="wms-principal" style={celda}>
                    <span style={{ display: 'grid', gap: 2 }}>
                      <span style={{ fontWeight: 500 }}>{e.rotulo}</span>
                      <span style={{ fontSize: 12, color: c.texto3 }}>lo creó {e.creadoPor}</span>
                    </span>
                  </td>
                  <td data-rotulo="Estado" style={celda}>
                    <EstadoChip estado={e.estado} tono={TONO_ENLACE[e.estado]} />
                  </td>
                  <td
                    data-rotulo="Archivos"
                    style={{ ...celda, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: c.texto2 }}
                  >
                    <Numero valor={e.archivos} /> de <Numero valor={e.maxArchivos} />
                  </td>
                  <td
                    data-rotulo="Vence"
                    style={{ ...celda, fontSize: 12.5, color: c.texto2, fontVariantNumeric: 'tabular-nums' }}
                  >
                    {fechaCorta(e.vence)}
                  </td>
                  <td className="wms-acciones" style={{ ...celda, textAlign: 'right' }}>
                    {e.estado === 'vigente' || e.estado === 'agotado' ? (
                      <button
                        type="button"
                        onClick={() => void revocar(e)}
                        style={{ ...botonSecundario, padding: '8px 14px', minHeight: 0, color: c.texto2 }}
                      >
                        Dar de baja
                      </button>
                    ) : (
                      <span style={{ fontSize: 12.5, color: c.texto3 }}>—</span>
                    )}
                  </td>
                </motion.tr>
              ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
      )}
      {dialogo}
    </div>
  );
}
